import { computed, type ComputedRef } from 'vue'
import { useRoute } from 'vue-router'

import { useCatalogStore } from '@/stores/catalog'

import { featureFlags } from '@/lib/featureFlags'

import { CartFlyoutEnhancement } from '@/types/CartFlyoutEnhancement'

export interface UseEnhancedFlyout {
  /** True where the enhanced cart flyout is active: auto-open, animation and toast suppression. */
  isEnhancedFlyoutActive: ComputedRef<boolean>
}

/**
 * The single source of truth for whether the enhanced cart flyout is active (SUN-292, Gap 9).
 *
 * Three behaviours resolve to this one switch, and they have to move together:
 *
 * - **Auto-open** — the flyout opening itself after an add.
 * - **The reward-unlock animation** — the sequence that plays over the tracker.
 * - **Toast suppression** — `useCartMutationFeedback`'s add/remove success toasts and the reward
 *   crossing toasts.
 *
 * The third is why this exists as one gate rather than three checks. Toast suppression is *coupled* to
 * the enhancement being active: an excluded route keeps today's behaviour wholesale, legacy toasts
 * included, because a cart that mutates silently *and* doesn't open is a cart with no feedback at all.
 * One switch makes that impossible to get half-right, and makes a later middle setting — open without
 * animating, say — a change in one place.
 *
 * ---
 *
 * **This gate is positional. It answers "where is the user?", not "what did they just do?"**
 *
 * That distinction is load-bearing and was nearly missed. Every clause here is a property of the user's
 * location — the feature flag, the route, the flow step. But the upsell pop-up rule (Gap 20) is *causal*:
 * an add taken inside the upsell modal must not auto-open the flyout, on the very routes that otherwise
 * would, because the customer is being moved on to the next page instead.
 *
 * One boolean cannot carry both axes, since the same route has to behave differently depending on what
 * triggered the mutation. So Gap 20 belongs in a **per-mutation suppression the caller passes**, not in
 * another clause here. Adding a `isUpsellModalOpen`-style check to this composable would be the wrong
 * shape: it would make a location-based question answer a cause-based one, and the answer would then
 * depend on modal state for every consumer, including ones that don't care.
 *
 * @see `docs/features/2026.08.04-cart-flyout-enhancement/decisions/2026.08.05-001-enhancement-gate-is-positional.md`
 */
export function useEnhancedFlyout(): UseEnhancedFlyout {
  const route = useRoute()
  const catalogStore = useCatalogStore()

  // The pose/background-selection step shares the `catalog-flow-step` route, so a route-name allow-list
  // cannot exclude it. Identifying it needs the param-to-step mapping and a bundle lookup, both of which
  // are catalog concerns — so the store answers it and this composable stays a composition of three
  // booleans.
  const isEnhancedFlyoutActive = computed(() => {
    const enhancement = route.meta.cartFlyoutEnhancement

    if (enhancement === undefined) return false

    // The kill switch restores *the storefront's* previous behaviour, which is a request only a route
    // with a previous behaviour can honour. The sandbox has none, so it opts out of the switch rather
    // than being emptied by it — see `CartFlyoutEnhancement`. Still positional: which flavour applies
    // is a property of where the user is, not of what they just did.
    if (enhancement === CartFlyoutEnhancement.WhenEnabled && !featureFlags.cartFlyoutEnhancement) {
      return false
    }

    return !catalogStore.isPoseBackgroundSelectionFlowStep(
      route.params.flowStep as string | undefined
    )
  })

  return { isEnhancedFlyoutActive }
}
